import { z } from "zod";
import { isoDateSchema } from "@common/helpers/date";
import {
    accountingReportMultiComparisonQuery,
    accountingReportQuery,
    generalLedgerFiltersSchema,
} from "@common/schemas/domains/accounting/report.schema";
import {
    arAgingSummaryQuery,
    arAgingDetailQuery,
} from "@common/schemas/domains/accounting/ar-aging-report.schema";
import {
    schedulesReportFiltersSchema,
    schedulesReportQuery,
} from "@common/schemas/domains/accounting/schedules-report.schema";
import { AccountingReportType } from "@common/types/reports/report-type";
import {
    REPORT_PACKAGE_MAX_ENTITIES,
    REPORT_PACKAGE_MAX_ROWS,
} from "@common/types/reports/report-package";
import {
    EXCEL_RESERVED_SHEET_NAME,
    findReportPackageSheetNameClashes,
    isReservedReportPackageSheetName,
    toReportPackageSheetName,
} from "@common/helpers/report-package-sheet-name";

export const reportPackageReportSettingSchema =
    accountingReportMultiComparisonQuery.pick({
        cycle: true,
        comparisons: true,
        comparisonDiffs: true,
        ratioTypes: true,
    });

export const reportPackageExportQuery = z.object({
    entityIds: z.array(z.coerce.number().int().positive()).min(1).max(100),
    reportTypes: z.array(z.nativeEnum(AccountingReportType)).min(1).max(10),
    startDate: z.string(),
    endDate: z.string(),
    ledgerId: z.string().uuid(),
    reportSettings: z
        .preprocess(
            (val) => {
                if (typeof val !== "string") {
                    return val;
                }
                try {
                    return JSON.parse(val);
                } catch {
                    return val;
                }
            },
            z
                .record(
                    z.nativeEnum(AccountingReportType),
                    reportPackageReportSettingSchema,
                )
                .optional(),
        )
        .optional(),
});

const reportPackageRowBaseSchema = z.object({
    entityId: z.number().int().positive().optional(),
    title: z
        .string()
        .trim()
        .min(1)
        .max(31)
        .refine((title) => toReportPackageSheetName(title) !== "", {
            message: "Title must contain at least one valid character.",
        })
        .refine((title) => !isReservedReportPackageSheetName(title), {
            message: `Use a title other than ${EXCEL_RESERVED_SHEET_NAME}`,
        }),
    ledgerId: z.string().uuid(),
    startDate: isoDateSchema.optional(),
    endDate: isoDateSchema.optional(),
});

const reportPackageStatementFiltersSchema = accountingReportQuery.pick({
    classes: true,
    classFilterType: true,
    counterpartiesIds: true,
    withEmptyOrUnverifiedCounterparty: true,
    hasCounterparty: true,
});

export type ReportPackageStatementFilters = z.infer<
    typeof reportPackageStatementFiltersSchema
>;

const reportPackageStatementRowSchema = reportPackageRowBaseSchema
    .merge(reportPackageReportSettingSchema)
    .extend({ filters: reportPackageStatementFiltersSchema.optional() });

const reportPackageProfitLossRowSchema = reportPackageStatementRowSchema.extend(
    {
        reportType: z.literal(AccountingReportType.PROFIT_LOSS),
    },
);

const reportPackageBalanceSheetRowSchema =
    reportPackageStatementRowSchema.extend({
        reportType: z.literal(AccountingReportType.BALANCE_SHEET),
    });

const reportPackageTrialBalanceRowSchema =
    reportPackageStatementRowSchema.extend({
        reportType: z.literal(AccountingReportType.TRIAL_BALANCE),
    });

const reportPackageCashFlowRowSchema = reportPackageStatementRowSchema.extend({
    reportType: z.literal(AccountingReportType.CASH_FLOW_STATEMENT),
});

const reportPackageExpenseByVendorRowSchema =
    reportPackageStatementRowSchema.extend({
        reportType: z.literal(AccountingReportType.EXPENSE_BY_VENDOR),
        filters: reportPackageStatementFiltersSchema
            .pick({
                classes: true,
                classFilterType: true,
            })
            .optional(),
    });

export type ReportPackageGeneralLedgerFilters = z.infer<
    typeof generalLedgerFiltersSchema
>;

const reportPackageGeneralLedgerRowSchema = reportPackageRowBaseSchema.extend({
    reportType: z.literal(AccountingReportType.GENERAL_LEDGER),
    filters: generalLedgerFiltersSchema.optional(),
});

const reportPackageArAgingSummaryRowSchema = reportPackageRowBaseSchema.extend({
    reportType: z.literal(AccountingReportType.AR_AGING_SUMMARY),
    filters: arAgingSummaryQuery
        .omit({ asOfDate: true, entityIds: true })
        .optional(),
});

const reportPackageArAgingDetailRowSchema = reportPackageRowBaseSchema.extend({
    reportType: z.literal(AccountingReportType.AR_AGING_DETAIL),
    filters: arAgingDetailQuery
        .omit({ asOfDate: true, entityIds: true })
        .optional(),
});

export type ReportPackageArAgingFilters = NonNullable<
    z.infer<typeof reportPackageArAgingDetailRowSchema>["filters"]
>;

const reportPackageSchedulesFiltersSchema = schedulesReportFiltersSchema.omit({
    holdingAccountIds: true,
});

export type ReportPackageSchedulesFilters = z.infer<
    typeof reportPackageSchedulesFiltersSchema
>;

const reportPackageSchedulesRowSchema = reportPackageRowBaseSchema.extend({
    reportType: z.literal(AccountingReportType.SCHEDULES),
    ...schedulesReportQuery.pick({
        cycle: true,
        rows: true,
        revenueSubMode: true,
        method: true,
    }).shape,
    layout: schedulesReportQuery.shape.layout.unwrap(),
    filters: reportPackageSchedulesFiltersSchema.optional(),
});

export const reportPackageRowSchema = z.discriminatedUnion("reportType", [
    reportPackageProfitLossRowSchema,
    reportPackageBalanceSheetRowSchema,
    reportPackageTrialBalanceRowSchema,
    reportPackageGeneralLedgerRowSchema,
    reportPackageExpenseByVendorRowSchema,
    reportPackageCashFlowRowSchema,
    reportPackageArAgingSummaryRowSchema,
    reportPackageArAgingDetailRowSchema,
    reportPackageSchedulesRowSchema,
]);

export const reportPackageRowsExportBody = z
    .object({
        entityIds: z
            .array(z.number().int().positive())
            .min(1)
            .max(REPORT_PACKAGE_MAX_ENTITIES),
        startDate: isoDateSchema,
        endDate: isoDateSchema,
        rows: z
            .array(reportPackageRowSchema)
            .min(1)
            .max(REPORT_PACKAGE_MAX_ROWS)
            .superRefine((rows, ctx) => {
                const clashingIndexes = findReportPackageSheetNameClashes(
                    rows.map((row) => row.title),
                );
                clashingIndexes.forEach((index) => {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Use a different title for each report",
                        path: [index, "title"],
                    });
                });
            }),
    })
    .refine(
        ({ rows, entityIds }) => {
            const rowEntityIds = rows.flatMap((row) =>
                row.entityId === undefined ? [] : [row.entityId],
            );
            return (
                rowEntityIds.length === 0 ||
                (rowEntityIds.length === rows.length &&
                    rowEntityIds.every((id) => entityIds.includes(id)) &&
                    entityIds.every((id) => rowEntityIds.includes(id)))
            );
        },
        {
            message:
                "Choose an entity for every row and include exactly those entities in the package",
        },
    )
    .refine(({ startDate, endDate }) => startDate <= endDate, {
        message: "Use a start date on or before the end date",
        path: ["startDate"],
    });
